import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import DateTimeHelpers from 'helpers/DateTimeHelpers';

export default function CustomDateFilterDialog({ open, onClose, onApply, initialFromDate, initialToDate }) {
  const { t: tCommon } = useTranslation('common');
  const { t: tPopup } = useTranslation('popup');

  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (open) {
      setSelectedStartDate(initialFromDate || new Date());
      setSelectedEndDate(initialToDate || new Date());
      setValidationError('');
    }
  }, [open, initialFromDate, initialToDate]);

  const handleApply = () => {
    if (!selectedStartDate || !selectedEndDate) {
      setValidationError(tPopup('Please select both From and To dates.'));
      return;
    }

    if (selectedStartDate > selectedEndDate) {
      setValidationError(tPopup('From Date cannot be after To Date.'));
      return;
    }

    const isExceeded = DateTimeHelpers.reportDateLimit(selectedStartDate, selectedEndDate);
    if (isExceeded) {
      setValidationError(
        tPopup('Reports can only be generated for up to 3 months. Please select a range within 3 months.')
      );
      return;
    }

    onApply(selectedStartDate, selectedEndDate);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{tPopup('Select Custom Date Range') || 'Select Custom Date Range'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2, minWidth: 320 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={tCommon('From Date') || 'From Date'}
            value={selectedStartDate}
            onChange={(newValue) => {
              setSelectedStartDate(newValue);
              setValidationError('');
            }}
            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
          />
          <DatePicker
            label={tCommon('To Date') || 'To Date'}
            value={selectedEndDate}
            onChange={(newValue) => {
              setSelectedEndDate(newValue);
              setValidationError('');
            }}
            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
          />
        </LocalizationProvider>
        {validationError && (
          <Typography color="error" variant="body2">
            {validationError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tCommon('Cancel')}</Button>
        <Button onClick={handleApply} variant="contained" color="primary">
          {tCommon('Apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
